import {
  type PackagingFieldType,
  IPackagingFieldsConfig,
  IFieldConfig,
  IRawPackagingConfig,
  IMiscellaneousConfig,
  PACKAGING_FIELDS,
  resolveMapsToPackageDetails,
  isPackageDetailsRowField,
} from "./types";

export type {
  IPackagingFieldsConfig,
  IFieldConfig,
  IRawPackagingConfig,
  IMiscellaneousConfig,
  PackagingFieldType,
};
export {
  PACKAGING_FIELDS,
  resolveMapsToPackageDetails,
  isPackageDetailsRowField,
};

/** Pickup “Process pickup” header badges: Bills first, then packaging fields in API `fields` order. */
export interface IPickupHeaderBadgeItem {
  key: string;
  label: string;
  value: string | number;
}

export type BadgeValue = string | number | null | undefined;

export type BadgeValues = Partial<Record<string, BadgeValue>>;

export interface BuildPickupHeaderBadgeItemsParams {
  packagingConfig: IPackagingFieldsConfig;
  values: BadgeValues;
}

export interface BuildPickupShipmentCardPackagingItemsParams {
  packagingConfig: IPackagingFieldsConfig;
  values: BadgeValues;
}

const PICKUP_HEADER_BADGE_PACKAGING_FIELDS: ReadonlySet<string> = new Set([
  PACKAGING_FIELDS.PIECES,
  PACKAGING_FIELDS.HU,
  PACKAGING_FIELDS.WEIGHT,
  PACKAGING_FIELDS.LOOSE_PIECES,
]);

const formatBadgeValue = (
  value: BadgeValue,
  whenEmpty: string | number
): string | number => (value === "" ? whenEmpty : value) ?? whenEmpty;

const getBadgeDisplayValue = (value: BadgeValue): string | number =>
  formatBadgeValue(value, "-");

const getShipmentCardPackagingDisplayValue = (value: BadgeValue): string | number =>
  formatBadgeValue(value, 0);

const toBadgeItem = (
  key: string,
  label: string,
  value: BadgeValue,
  formatValue: (v: BadgeValue) => string | number = getBadgeDisplayValue
): IPickupHeaderBadgeItem => ({
  key,
  label,
  value: formatValue(value),
});

export const parsePackagingFieldsConfig = (
  raw?: string | IPackagingFieldsConfig
): IPackagingFieldsConfig | null => {
  if (!raw) return null;

  const parsed: any =
    typeof raw === "string" ? JSON.parse(raw) : raw;

  if (parsed.fieldOrder && parsed.fieldMap) {
    return parsed as IPackagingFieldsConfig;
  }

  const fieldOrder = parsed.fields.map((f: IFieldConfig) => f.field);
  const fieldMap: Record<string, IFieldConfig> = {};
  for (const f of parsed.fields as IFieldConfig[]) {
    fieldMap[f.field] = f;
  }

  return {
    fieldOrder,
    fieldMap,
    miscellaneous: parsed.miscellaneous as IMiscellaneousConfig,
  };
};

// Returns false when config is missing, unparsable, the field is absent from fieldMap, or visible is false
export const isPackagingFieldVisible = (
  pickupPackagingDetailsConfig: string | IPackagingFieldsConfig | null | undefined,
  fieldName: string
): boolean => {
  try {
    const config = parsePackagingFieldsConfig(
      pickupPackagingDetailsConfig as string | IPackagingFieldsConfig | undefined
    );
    if (!config) return false;
    return Boolean(config.fieldMap[fieldName]?.visible);
  } catch {
    return false;
  }
};

const buildPackagingSummaryDisplayItems = (
  packagingConfig: IPackagingFieldsConfig,
  values: BadgeValues,
  formatValue: (v: BadgeValue) => string | number = getBadgeDisplayValue
): IPickupHeaderBadgeItem[] =>
  packagingConfig.fieldOrder
    .filter((field) => PICKUP_HEADER_BADGE_PACKAGING_FIELDS.has(field))
    .filter((field) => packagingConfig.fieldMap[field]?.visible)
    .map((field) => {
      const cfg = packagingConfig.fieldMap[field];
      return toBadgeItem(field, cfg.shortLabel, values[field], formatValue);
    });

// Pickup header badges: **Bills** first (`values.bills`), then visible packaging fields in API order.
export const buildPickupHeaderBadgeItems = ({
  packagingConfig,
  values,
}: BuildPickupHeaderBadgeItemsParams): IPickupHeaderBadgeItem[] => [
  toBadgeItem("bills", "Bills", values.bills),
  ...buildPackagingSummaryDisplayItems(packagingConfig, values),
];

// Pickup shipment card title line: visible packaging fields in API order (no Bills). Pass `hu` as the sum of `packagingItems[].hu` when the HU field is visible.
export const buildPickupShipmentCardPackagingItems = ({
  packagingConfig,
  values,
}: BuildPickupShipmentCardPackagingItemsParams): IPickupHeaderBadgeItem[] =>
  buildPackagingSummaryDisplayItems(
    packagingConfig,
    values,
    getShipmentCardPackagingDisplayValue
  );

export const getFieldConfig = (
  config: IPackagingFieldsConfig,
  fieldName: string
): IFieldConfig => {
  const cfg = config.fieldMap[fieldName];
  if (!cfg) {
    throw new Error(`Packaging config missing required field: "${fieldName}"`);
  }
  return cfg;
};

interface IEditInfo {
  pieces?: string | number;
  weight?: string | number;
  loosePieces?: string | number;
  packagingItems?: Array<{hu?: string | number; loosePieces?: string | number}>;
}

type PackagingItemRowTotal = NonNullable<IEditInfo["packagingItems"]>[number];

type PackageRowSumField =
  | typeof PACKAGING_FIELDS.HU
  | typeof PACKAGING_FIELDS.LOOSE_PIECES;

const sumPackageRowsByField = (
  items: PackagingItemRowTotal[],
  key: PackageRowSumField
): number =>
  items.reduce(
    (sum, item) => sum + (parseInt(String(item[key])) || 0),
    0
  );

// hu and loosePieces summed from packagingItems[] when per-row; loosePieces falls back to shipment-level when standalone
export const resolveCardSummaryValues = (
  editInfo: IEditInfo,
  packagingConfig: IPackagingFieldsConfig
): BadgeValues => {
  const packagingItems = editInfo.packagingItems ?? [];

  const huTotal = sumPackageRowsByField(packagingItems, PACKAGING_FIELDS.HU);

  const loosePiecesCfg = packagingConfig.fieldMap[PACKAGING_FIELDS.LOOSE_PIECES];
  const loosePiecesIsPerRow =
    loosePiecesCfg !== undefined &&
    isPackageDetailsRowField(loosePiecesCfg);
  const loosePiecesTotal = loosePiecesIsPerRow
    ? sumPackageRowsByField(packagingItems, PACKAGING_FIELDS.LOOSE_PIECES)
    : editInfo.loosePieces;

  return {
    pieces: editInfo.pieces,
    hu: huTotal,
    weight: editInfo.weight,
    loosePieces: loosePiecesTotal,
  };
};

export const calculateTotal = (
  editInfo: IEditInfo,
  sourceFields: string[] = []
): number => {
  let total = 0;
  const packagingItems = editInfo.packagingItems ?? [];
  if (sourceFields.includes(PACKAGING_FIELDS.PIECES)) {
    total += parseInt(String(editInfo.pieces)) || 0;
  }
  if (sourceFields.includes(PACKAGING_FIELDS.LOOSE_PIECES)) {
    // Standalone (shipment-level) loosePieces OR per-row loosePieces on packagingItems — only one is non-zero for any given config
    total += parseInt(String(editInfo.loosePieces)) || 0;
    total += sumPackageRowsByField(packagingItems, PACKAGING_FIELDS.LOOSE_PIECES);
  }
  if (sourceFields.includes(PACKAGING_FIELDS.HU)) {
    total += sumPackageRowsByField(packagingItems, PACKAGING_FIELDS.HU);
  }
  return total;
};
